import endpoint, { type ErrorResponse } from 'utils/endpoint';
import { HttpRequestMethodTypes } from 'types';
import { dasherizeKeys } from '@zoocasa/node-kit/objects';
import { useTranslation } from 'next-i18next';
import { LOCALE_FORM_ERROR_PATHS, Namespaces } from 'types/locales';
import { withTryAsync } from 'utils/withTry';
import { useThemeContext } from 'contexts';
import { useAuth } from '@bedrock/auth';

import type { User } from '@bedrock/auth';

type JSONAPIPayload = {
  data: {
    id: string | number;
    attributes: {
      firstName: string;
      lastName: string;
      homePhone: string;
      email: string;
      jwt: string;
      newsletter: boolean;
      ssoProvider: string | null;
      hasPassword: boolean;
      hideImagelessListings: boolean;
      locale: string;
    };
  };
};

type Success = { success: string };
type Error = { error: string };

export interface ProfileActions {
  confirmUser: (confirmationToken: string, userAgent: string, userLocationSlug: string, userLatitude: number, userLongitude: number) => Promise<Success | Error>;
  resetPassword: (params: { password: string; passwordConfirmation: string; resetPasswordToken: string }) => Promise<Success | Error>;
  updatePassword: (params: { currentPassword?: string; password: string; passwordConfirmation: string; email?: string }) => Promise<Success | Error>;
  updateUserData: (params: User) => Promise<Success | Error>;
  updateEmail: (params: { newEmail: string; retypeEmail: string; password?: string; passwordConfirmation?: string }) => Promise<Success | Error>;
  confirmEmailUpdate: (code: string) => Promise<Success | Error>;
}

export function useProfile(user: User | null): ProfileActions {
  const authActions = useAuth();
  const { themeName } = useThemeContext();
  const { t } = useTranslation(Namespaces.FORM_ERROR);
  const { supportedFeatures } = authActions;

  const parseError = (error: any): string => {
    if (error?.source?.pointer && error.title) {
      const pointer = error.source.pointer.split('/').pop();
      const capitalizedPointer = pointer.charAt(0).toUpperCase() + pointer.slice(1);
      return `${capitalizedPointer.replace(/-/g, ' ')} ${error.title}`;
    }
    return t(LOCALE_FORM_ERROR_PATHS.GENERAL_ERROR, { defaultValue: 'Something went wrong. Please try again later.' });
  };

  const confirmUser: ProfileActions['confirmUser'] = async (confirmationToken, userAgent, userLocationSlug, userLatitude, userLongitude) => {
    const params = { confirmationToken, userAgent, userLocationSlug, userLatitude, userLongitude, theme: themeName };
    try {
      const response = await endpoint<JSONAPIPayload>('/services/api/v3/users/confirmation', HttpRequestMethodTypes.GET, params);
      const confirmedUser: User = { id: response.data.id, ...response.data.attributes };
      authActions.setUser(confirmedUser);
      return { success: t(LOCALE_FORM_ERROR_PATHS.ACCOUNT_CONFIRMATION_SUCCESS, { defaultValue: 'Your account confirmation was successful. You are now logged in.' }) };
    } catch {
      return { error: t(LOCALE_FORM_ERROR_PATHS.ACCOUNT_CONFIRMATION_FAILED, { defaultValue: 'Your account confirmation was not successful.' }) };
    }
  };

  const resetPassword: ProfileActions['resetPassword'] = async params => {
    const [response, error] = await withTryAsync<object, ErrorResponse>(endpoint)('/services/api/v3/users/password', HttpRequestMethodTypes.PATCH, { ...params, theme: themeName });
    if (error || 'error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.RESET_TOKEN_INVALID, { defaultValue: 'Reset Password Token is invalid.' }) };
    }
    return { success: t(LOCALE_FORM_ERROR_PATHS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' }) };
  };

  const updatePassword: ProfileActions['updatePassword'] = async params => {
    const [response, error] = await withTryAsync<JSONAPIPayload, ErrorResponse>(endpoint)('/services/api/v3/users/password', HttpRequestMethodTypes.PATCH, { ...params, theme: themeName });
    if (error) {
      return { error: parseError(error.errors?.[0]) };
    } else if ('error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.GENERAL_ERROR, { defaultValue: 'Something went wrong. Please try again later.' }) };
    }
    if (response.data) {
      const updatedUser: User = { id: response.data.id, ...response.data.attributes };
      authActions.setUser(updatedUser);
    }
    return { success: t(LOCALE_FORM_ERROR_PATHS.PASSWORD_CHANGED_SUCCESS, { defaultValue: 'Your password was changed successfully.' }) };
  };

  const updateUserData: ProfileActions['updateUserData'] = async params => {
    if (supportedFeatures.canUpdateProfile) {
      const result = await authActions.updateProfile(params);
      if (result.ok === true) {
        authActions.setUser(params);
        return { success: t(LOCALE_FORM_ERROR_PATHS.INFO_CHANGED_SUCCESS, { defaultValue: 'Your info was changed successfully.' }) };
      }
      return { error: result.error };
    }

    const [response, error] = await withTryAsync<JSONAPIPayload, ErrorResponse>(endpoint)(`/services/api/v3/users/${params.id}`, HttpRequestMethodTypes.PATCH, { data: { attributes: dasherizeKeys(params) }});
    if (error || 'error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.IS_INVALID, { defaultValue: 'is invalid' }) };
    }
    const updatedUser: User = { id: params.id, ...params };
    authActions.setUser(updatedUser);
    return { success: t(LOCALE_FORM_ERROR_PATHS.INFO_CHANGED_SUCCESS, { defaultValue: 'Your info was changed successfully.' }) };
  };

  const updateEmail: ProfileActions['updateEmail'] = async params => {
    if (supportedFeatures.canUpdateEmail) {
      const result = await authActions.updateEmail(user!, params.newEmail, params.retypeEmail, params.password, params.passwordConfirmation);
      if (result.ok === true) {
        return { success: t(LOCALE_FORM_ERROR_PATHS.EMAIL_CHANGED_SUCCESS, { defaultValue: 'Your email was changed successfully.' }) };
      }
      return { error: result.error };
    }

    const data = {
      data: {
        type: 'users',
        attributes: {
          currentEmail: user?.email,
          email: params.newEmail,
          emailConfirmation: params.retypeEmail,
          password: params.password,
          passwordConfirmation: params.passwordConfirmation,
        },
      },
    };

    const [response, error] = await withTryAsync<JSONAPIPayload, ErrorResponse>(endpoint)('/services/api/v3/users/email', HttpRequestMethodTypes.PATCH, data);
    if (error) {
      return { error: parseError(error.errors?.[0]) };
    } else if ('error' in response) {
      return { error: t(LOCALE_FORM_ERROR_PATHS.IS_INVALID, { defaultValue: 'is invalid' }) };
    }
    if (user) {
      const updatedUser: User = { ...user, email: params.newEmail, hasPassword: true };
      authActions.setUser(updatedUser);
    }
    return { success: t(LOCALE_FORM_ERROR_PATHS.EMAIL_CHANGED_SUCCESS, { defaultValue: 'Your email was changed successfully.' }) };
  };

  const confirmEmailUpdate: ProfileActions['confirmEmailUpdate'] = async code => {
    const result = await authActions.confirmEmailUpdate(code);
    if (result.ok === true) {
      return { success: t(LOCALE_FORM_ERROR_PATHS.EMAIL_CHANGED_SUCCESS, { defaultValue: 'Your email was changed successfully.' }) };
    }
    return { error: result.error };
  };

  return { confirmUser, resetPassword, updatePassword, updateUserData, updateEmail, confirmEmailUpdate };
}
